import type { Persona } from '@tempo-io/sir-dto';
import { CURRENT_PERSONA_SCHEMA_VERSION } from '@tempo-io/sir-dto';

/**
 * The built-in roles offered by the Configure roles dialog. Role content lives here and nowhere
 * else — the backend stores whatever an org saves and ships no defaults of its own, so an org that
 * has never saved a role set cannot produce a run.
 *
 * Labels are role titles rather than the names of the people who hold them, so the defaults mean
 * the same thing in every org.
 */

const NO_COST_DATA = 'Never invent a dollar figure — Loop exposes no cost data.';

const role = (persona: Omit<Persona, 'version' | 'isCore'> & { isCore?: boolean }): Persona => ({
  ...persona,
  version: CURRENT_PERSONA_SCHEMA_VERSION,
  isCore: persona.isCore ?? false,
});

export const DEFAULT_ROLES: Persona[] = [
  role({
    id: 'vp_product',
    label: 'VP of Product',
    isCore: true,
    headline: 'Your portfolio, read from the OKRs down.',
    lens: 'Objective execution, initiative/feature-level detail — this IS the default shared-core lens every finding is authored from.',
    recommendationStyle:
      "The root-level recommendations on each finding are this role's voice directly — no separate override is needed or expected.",
    discoveryHints:
      'None — this is the baseline top-down discovery pass every finding is built from, not a supplementary sweep.',
    relevanceNotes:
      'Sees every shared-core finding by definition; this role is never trimmed from a shared finding.',
    avoid: NO_COST_DATA,
  }),
  role({
    id: 'ceo',
    label: 'CEO',
    headline: 'Your biggest bets, read in one page.',
    lens: 'Board and narrative stakes, cross-functional escalation — does this need direct CEO attention or a leadership sync, versus something that resolves at a level below.',
    recommendationStyle:
      'Escalate to the right owner; brief the board or leadership sync; decide now whether to resource or formally re-scope. Frame misses as deliberate reprioritization, never a silent surprise.',
    discoveryHints:
      'No dedicated exclusive-discovery sweep by default — the shared objective-linked risk/opportunity/ontrack set is sufficient at this altitude.',
    relevanceNotes:
      "Skip narrow, single-module tracking or hygiene findings that don't carry real board-level stakes. Applies to anything with genuine cross-functional or external-narrative risk.",
    avoid: NO_COST_DATA,
  }),
  role({
    id: 'cpo',
    label: 'CPO',
    headline: 'Your roadmap, minus the wishful thinking.',
    lens: 'Roadmap confidence, prioritization, cross-initiative tradeoffs.',
    recommendationStyle:
      'Reprioritize explicitly rather than letting things drift; re-confirm dates with the owning team before they are communicated further; require objective-linkage as a gate on new roadmap items.',
    discoveryHints:
      'Check the Roadmap Bucket custom field distribution across top-level initiatives — a real signal on whether investment is going to parity or to differentiation.',
    relevanceNotes: 'Applies broadly across roadmap-shaping findings.',
    avoid: NO_COST_DATA,
  }),
  role({
    id: 'cto',
    label: 'CTO',
    headline: "Where the platform's technical risk actually lives.",
    lens: 'The high-level signal on technology and architecture risk — is a foundational data model or integration being built carelessly under launch pressure, is technical debt tracked honestly, is security posture a one-time scan or a standing process. Wants the headline, not the deep-dive. Distinct from the VP of Engineering’s sprint-level tactical lens: this role cares whether the structure underneath delivery is sound, not whether this cycle is unblocked.',
    recommendationStyle:
      'Default action is delegation, not a personal deep-dive: ask the VP of Engineering or the relevant VP/Director-level owner to look into it and report back. Recommendations should read as a request for a readout, not a prescribed technical fix.',
    discoveryHints:
      'Sweep for a known architecture-migration or technical-debt effort by name, then compare the item meant to track that work against the broader name-based sweep to find scope hiding outside the tracked hierarchy — that gap, not raw progress, is the signal.',
    relevanceNotes:
      'Applies broadly to anything with real architectural or technical-debt stakes — but keep every recommendation at the "ask for a readout" level, even when the underlying finding is deeply technical.',
    avoid: `${NO_COST_DATA} Do not recommend that this role personally review architecture or investigate a finding — the action should route through the VP of Engineering or another VP/Director-level owner.`,
  }),
  role({
    id: 'cfo',
    label: 'CFO',
    headline: 'Where the budget is actually going.',
    lens: 'Cost and resourcing efficiency, budget risk, forecast reliability.',
    recommendationStyle:
      'Require a bounded, specific resourcing ask rather than an open-ended one; treat any estimate the org itself flags low-confidence as a range, not a number.',
    discoveryHints:
      'Check the distribution of the Estimate Confidence custom field across initiatives — distinct from items with no estimate at all. This is about confidence in estimates that do exist.',
    relevanceNotes:
      'Applies to anything with real resourcing or estimate-confidence stakes; skip narrow single-module tracking findings with no resourcing angle.',
    avoid: NO_COST_DATA,
  }),
  role({
    id: 'cro',
    label: 'CRO',
    headline: "What's actually going to close deals.",
    lens: 'Revenue timing, deal risk, whether a date or capability is safe to put in front of a prospect or cite as a sales commitment.',
    recommendationStyle:
      'Get commitment dates confirmed with the owning team before they reach a customer; flag any date that risks being oversold.',
    discoveryHints:
      'Check Target Persona custom field coverage — how many initiatives have no defined buyer or user persona at all, and how concentrated any specific economic-buyer persona is.',
    relevanceNotes:
      'Applies broadly; skip internal-only hygiene findings with no customer-facing or deal-relevant angle.',
    avoid: NO_COST_DATA,
  }),
  role({
    id: 'cai',
    label: 'CAI',
    headline: 'Where our AI bets actually stand.',
    lens: 'The AI/ML-tagged slice of the portfolio specifically — is AI work progressing, stalled, or unowned, independent of how the rest of the portfolio looks.',
    recommendationStyle:
      'Isolate AI-specific ownership or architecture questions rather than letting them ride on a generic prioritization call; compare a stalled AI initiative against a working one as a diagnostic.',
    discoveryHints:
      "Cross-reference the AI and agent boolean custom fields to find AI-tagged initiatives, then check their completion status specifically — don't assume AI-tagged work is progressing just because the portfolio overall looks healthy.",
    relevanceNotes: 'Applies broadly to anything touching AI-tagged scope.',
    avoid: NO_COST_DATA,
  }),
  role({
    id: 'vp_eng',
    label: 'VP of Engineering',
    headline: "What's actually blocking delivery right now.",
    lens: 'Resourcing, blockers, technical process — is this a prioritization call or a capacity gap.',
    recommendationStyle:
      'Assign an owner directly; run a capacity review; fix the tracking bug or process gap immediately rather than escalating it.',
    discoveryHints:
      'Sweep by item type for security and vulnerability items across ALL statuses, not just what happens to surface natively — undercounts here are common.',
    relevanceNotes: 'Applies broadly to delivery and execution findings.',
    avoid: NO_COST_DATA,
  }),
];

export const isBuiltInRoleId = (id: string): boolean =>
  DEFAULT_ROLES.some((persona) => persona.id === id);
